import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MaskedLines } from "@/components/motion";
import { useEnquiry } from "@/context/EnquiryContext";

export default function Hero({ home }) {
  const slides = home?.hero_slides?.length ? home.hero_slides : [];
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);
  const nav = useNavigate();
  const { openEnquiry } = useEnquiry();

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.45, 0.75]);

  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 5500);
    return () => clearInterval(t);
  }, [slides.length]);

  const headline = home?.hero_headline || "Discover Incredible India";
  const words = headline.split(" ");
  const lines = words.length > 2
    ? [words.slice(0, Math.ceil(words.length / 2)).join(" "), words.slice(Math.ceil(words.length / 2)).join(" ")]
    : [headline];

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#2d1616]">
      {/* Parallax slides */}
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <AnimatePresence mode="sync">
          {slides.map((s, i) =>
            i === idx ? (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.4, ease: "easeInOut" }}
                className="absolute inset-0"
              >
                <img src={s.image} alt={s.title || "India"} className="w-full h-full object-cover" />
              </motion.div>
            ) : null
          )}
        </AnimatePresence>
      </motion.div>

      {/* Gradient overlays */}
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-gradient-to-t from-[#2d1616] via-[#2d1616]/30 to-[#2d1616]/50" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#2d1616]/60 to-transparent" />

      {/* Content */}
      <div className="relative h-full max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-col justify-end pb-20 sm:pb-28">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="flex items-center gap-3 mb-6">
          <span className="h-px w-12 bg-[#d4af37]" />
          <span className="text-[11px] sm:text-xs tracking-[0.34em] uppercase text-[#e8c970] font-semibold">
            Tailor-Made India Journeys · Trusted Worldwide
          </span>
        </motion.div>

        <h1 className="font-serif text-white text-[3rem] leading-[0.98] sm:text-7xl lg:text-[6.5rem] tracking-tight max-w-5xl">
          <MaskedLines lines={lines} delay={0.35} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-6 text-base sm:text-lg text-white/85 max-w-xl tracking-wide"
        >
          {home?.hero_subheadline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-9 flex flex-col sm:flex-row gap-4"
        >
          <button onClick={() => nav("/tours")} data-testid="hero-explore" className="group inline-flex items-center justify-center gap-3 bg-[#7c1d1d] hover:bg-[#5a1515] text-white px-8 py-4 text-xs tracking-[0.26em] uppercase transition-colors border border-[#d4af37]/50">
            Explore Tours <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <button onClick={() => openEnquiry()} data-testid="hero-plan" className="inline-flex items-center justify-center gap-2 px-8 py-4 text-xs tracking-[0.26em] uppercase text-white border border-white/50 hover:bg-white hover:text-[#3b1a1a] transition-colors">
            Plan My Trip
          </button>
        </motion.div>
      </div>

      {/* Slide indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-8 right-5 sm:right-8 z-10 flex items-center gap-3">
          <span className="font-serif text-white/80 text-sm">{String(idx + 1).padStart(2, "0")}</span>
          <div className="flex gap-2">
            {slides.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`}
                className={`h-[2px] transition-all duration-500 ${i === idx ? "w-8 bg-[#d4af37]" : "w-4 bg-white/40"}`} />
            ))}
          </div>
          <span className="font-serif text-white/50 text-sm">{String(slides.length).padStart(2, "0")}</span>
        </div>
      )}

      {/* Scroll cue */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 text-white/70">
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ChevronDown className="w-5 h-5" />
        </motion.div>
      </motion.div>
    </section>
  );
}
